<template>
  <div class="w-full max-w-full p-2 sm:p-4 lg:p-6">
    <!-- Page Title -->
    <div class="font-semibold text-lg sm:text-xl lg:text-2xl mb-3 sm:mb-4 lg:mb-6 text-gray-800">
      {{ staffLoc }}
    </div>

    <!-- Responsive DataTable Container -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <DataTable
        :value="customers1"
        :paginator="true"
        :rows="10"
        dataKey="id"
        :rowHover="true"
        v-model:filters="filters1"
        @filter="onTableFilter"
        filterDisplay="menu"
        :loading="loading1"
        :filters="filters1"
        :globalFilterFields="[
          'fname',
          'lname',
          'mname',
          'email',
          'phone_number',
          'school.name',
          'address1',
          'address2',
          'marital_status',
          'gender',
          'barangay',
        ]"
        showGridlines
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        :rowsPerPageOptions="[5, 10, 25, 100, 1000, 10000]"
        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} staffs"
        class="responsive-datatable"
      >
        <template #header>
          <!-- Mobile Header (Stacked Layout) -->
          <div class="block lg:hidden p-3 space-y-3 bg-gray-50 border-b">
            <!-- Search -->
            <div class="w-full">
              <IconField>
                <InputIcon>
                  <i class="pi pi-search text-sm" />
                </InputIcon>
                <InputText
                  v-model="filters1['global'].value"
                  placeholder="Search staff..."
                  class="w-full text-sm"
                />
              </IconField>
            </div>

            <!-- Filters Grid -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <MultiSelect
                v-model="staffRankValue"
                :options="staffRankList"
                placeholder="Rank"
                class="w-full text-xs"
              />
              <MultiSelect
                v-model="staffDepartmentValue"
                :options="staffDepartmentList"
                placeholder="Department"
                class="w-full text-xs"
              />
              <MultiSelect
                v-model="staffemploymentValue"
                :options="staffEmploymentList"
                placeholder="Employment"
                class="w-full text-xs"
              />
              <Button
                label="Filter"
                severity="info"
                raised
                class="w-full text-xs py-2"
                size="small"
              />
            </div>

            <!-- Action Button -->
            <div class="w-full">
              <Button
                v-if="authStore.roles.includes('addStaff')"
                label="Add Staff"
                severity="info"
                raised
                @click="router.push({ path: '/staff/add', query: { isOverAll: true } })"
                class="w-full text-sm"
                icon="pi pi-plus"
                size="small"
              />
            </div>
          </div>

          <!-- Desktop Header (Original Layout) -->
          <div class="hidden lg:block p-4 bg-gray-50 border-b">
            <div class="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-3">
              <!-- Filters Section -->
              <div class="flex flex-wrap gap-3 flex-1">
                <IconField>
                  <InputIcon>
                    <i class="pi pi-search" />
                  </InputIcon>
                  <InputText
                    v-model="filters1['global'].value"
                    placeholder="Keyword Search"
                    class="w-80 xl:w-96"
                  />
                </IconField>
                <MultiSelect
                  v-model="staffRankValue"
                  :options="staffRankList"
                  placeholder="Staff Rank"
                  class="w-48"
                />
                <MultiSelect
                  v-model="staffDepartmentValue"
                  :options="staffDepartmentList"
                  placeholder="Staff Department"
                  class="w-48"
                />
                <MultiSelect
                  v-model="staffemploymentValue"
                  :options="staffEmploymentList"
                  placeholder="Staff Employment"
                  class="w-48"
                />
                <Button
                  label="Submit Filters"
                  severity="info"
                  raised
                  @click="submitFilter"
                  class="whitespace-nowrap"
                />
              </div>

              <!-- Action Button -->
              <div class="lg:ml-4">
                <Button
                  v-if=" authStore.roles.includes('addStaff')"
                  label="Add Staff"
                  severity="info"
                  raised
                  @click="router.push({ path: '/staff/add', query: { isOverAll: true } })"
                  class="whitespace-nowrap"
                />
              </div>
            </div>
          </div>
        </template>

        <template #empty>
          <div class="text-center py-6 text-gray-500">
            <i class="pi pi-users text-3xl mb-3 block text-gray-400"></i>
            <p class="text-base font-medium">No staff found</p>
            <p class="text-sm text-gray-400">Try adjusting your search criteria</p>
          </div>
        </template>

        <template #loading>
          <div class="text-center py-6">
            <i class="pi pi-spin pi-spinner text-2xl text-blue-500 mb-2"></i>
            <p class="text-gray-600">Loading staff data. Please wait.</p>
          </div>
        </template>

        <!-- Name Column - Always visible -->
        <Column
          field="name"
          filterField="fname"
          filter-field="lname"
          header="Name"
          style="min-width: 180px"
          frozen
          class="name-column"
        >
          <template #body="{ data }">
            <div class="text-lg sm:text-sm lg:text-base font-medium text-gray-900 leading-tight">
              <div class="truncate max-w-[120px] sm:max-w-[160px] lg:max-w-none" :title="data.mname ? `${data.lname}, ${data.fname} ${data.mname}` : `${data.lname}, ${data.fname}`">
                {{ data.mname ? `${data.lname}, ${data.fname} ${data.mname}` : `${data.lname}, ${data.fname}` }}
              </div>
              <!-- Mobile-only additional info -->
              <div class="block md:hidden text-sm text-gray-500 mt-1">
                <div class="text-base font-medium text-blue-700 leading-tight">
                  {{ data.school?.name }}
                </div>
                <div>{{ data.gender }} • {{ data.phone_number }}</div>
                <div class="truncate">{{ data.email }}</div>

            <div class="block mt-4 md:hidden">
              <div class="flex flex-col gap-1">
                <Button
                  v-if="authStore.roles.includes('viewStaff')"
                  type="button"
                  label="View"
                  icon="pi pi-eye"
                  @click="router.push(`/staff/profile/${data.pid}`)"
                  severity="primary"
                  size="small"
                  class="w-full text-xs py-1"
                />
                <Button
                  v-if="authStore.roles.includes('editStaff')"
                  type="button"
                  label="Comment"
                  icon="pi pi-comments"
                  @click="showDialog1 = true"
                  severity="secondary"
                  size="small"
                  class="w-full text-xs py-1"
                />
              <Button
                v-if="authStore.roles.includes('deleteStaff')"
                type="button"
                label="Delete"
                icon="pi pi-comments"
                v-tooltip="'Delete Staff'"
                @click="requestDelete(data.pid)"
                severity="danger"
                size="small"
              />
              </div>
            </div>
              </div>
            </div>
          </template>
        </Column>

        <!-- School Column - Always visible (important for overall staff list) -->
        <Column
          header="School"
          filterField="school.name"
          style="min-width: 150px"
          frozen
          class="hidden md:table-cell"
        >
          <template #body="{ data }">
            <div class="text-xs sm:text-sm lg:text-base font-medium text-blue-700 leading-tight">
              <div class="truncate max-w-[100px] sm:max-w-[130px] lg:max-w-none" :title="data.school?.name">
                {{ data.school?.name  || 'N/A' }}
              </div>
              <!-- Mobile-only additional info -->
              <div class="block md:hidden text-xs text-gray-500 mt-1">
                <div>{{ data.marital_status || 'N/A' }}</div>
              </div>
            </div>
          </template>
        </Column>

        <!-- Gender Column - Hidden on mobile -->
        <Column
          header="Gender"
          filterField="gender"
          style="min-width: 80px"
          class="hidden md:table-cell"
        >
          <template #body="{ data }">
            <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                  :class="data.gender === 'Male' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'">
              {{ data.gender }}
            </span>
          </template>
        </Column>

        <!-- Marital Status Column - Hidden on mobile and small tablets -->
        <Column
          header="Marital Status"
          filterField="marital_status"
          style="min-width: 120px"
          class="hidden lg:table-cell"
        >
          <template #body="{ data }">
            <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
              {{ data.marital_status || 'N/A' }}
            </span>
          </template>
        </Column>

        <!-- Phone Number Column - Hidden on mobile and tablets -->
        <Column
          header="Phone"
          filterField="phone_number"
          style="min-width: 120px"
          class="hidden lg:table-cell"
        >
          <template #body="{ data }">
            <div class="text-sm text-gray-600">
              {{ data.phone_number || 'N/A' }}
            </div>
          </template>
        </Column>

        <!-- Email Column - Hidden on mobile and tablets -->
        <Column
          header="Email"
          filterField="email"
          style="min-width: 180px"
          class="hidden xl:table-cell"
        >
          <template #body="{ data }">
            <div class="text-sm text-gray-600 truncate max-w-[150px]" :title="data.email">
              {{ data.email || 'N/A' }}
            </div>
          </template>
        </Column>

        <!-- Barangay Column - Hidden on mobile and tablets -->
        <Column
          header="Role"
          filterField="position_role.name"
          style="min-width: 120px"
          class="hidden 2xl:table-cell"
        >
          <template #body="{ data }">
            <div class="text-sm text-gray-600 truncate max-w-[100px]" :title="data.position_role?.name">
              {{ data.position_role?.name || 'N/A' }}
            </div>
          </template>
        </Column>

        <!-- Address Column - Hidden on mobile and tablets -->
        <Column
          header="Address"
          filterField="address1"
          filter-field="address2"
          style="min-width: 160px"
          class="hidden xl:table-cell"
        >
          <template #body="{ data }">
            <div class="text-sm text-gray-600">
              <div class="truncate max-w-[140px]" :title="`${data.address1} ${data.address2}`.trim()">
                {{ `${data.address1} ${data.address2}`.trim() || 'N/A' }}
              </div>
            </div>
          </template>
        </Column>


        <!-- Actions Column - Always visible -->
        <Column
          header="Actions"
          bodyClass="text-center"
          style="min-width: 120px"
          class="hidden md:table-cell"
        >
          <template #body="{ data }">
            <!-- Mobile Actions - Stacked -->
            <div class="block md:hidden">
              <div class="flex flex-col gap-1">
                <Button
                  v-if="authStore.roles.includes('viewStaff')"
                  type="button"
                  label="View"
                  icon="pi pi-eye"
                  @click="router.push(`/staff/profile/${data.pid}`)"
                  severity="primary"
                  size="small"
                  class="w-full text-xs py-1"
                />
                <Button
                  v-if="authStore.roles.includes('editStaff')"
                  type="button"
                  label="Comment"
                  icon="pi pi-comments"
                  @click="showDialog1 = true"
                  severity="secondary"
                  size="small"
                  class="w-full text-xs py-1"
                />
              <Button
                v-if="authStore.roles.includes('deleteStaff')"
                type="button"
                label="Delete"
                icon="pi pi-comments"
                v-tooltip="'Delete Staff'"
                @click="requestDelete(data.pid)"
                severity="danger"
                size="small"
              />
              </div>
            </div>

            <!-- Desktop Actions - Horizontal -->
            <div class="hidden md:flex justify-center items-center gap-2">
              <Button
                v-if=" authStore.roles.includes('viewStaff')"
                type="button"
                label="View"
                icon="pi pi-eye"
                v-tooltip="'View Profile'"
                @click="router.push(`/staff/profile/${data.pid}`)"
                severity="primary"
                size="small"
              />
              <!-- <Button
                v-if="authStore.roles.includes('editStaff')"
                type="button"
                label="Comment"
                icon="pi pi-comments"
                v-tooltip="'Add Comment'"
                @click="showDialog1 = true"
                severity="secondary"
                size="small"
              /> -->
              <Button
                v-if="authStore.roles.includes('deleteStaff')"
                type="button"
                label="Delete"
                icon="pi pi-times"
                v-tooltip="'Delete Staff'"
                @click="requestDelete(data.pid)"
                severity="danger"
                size="small"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Comments Dialog -->
    <Dialog
      v-model:visible="showDialog1"
      modal
      :style="getDialogStyle()"
      :breakpoints="{ '960px': '75vw', '641px': '95vw' }"
      header="Health Comments"
      class="responsive-dialog"
    >
      <health-comments @close="showDialog1 = false" />
    </Dialog>

            <Dialog header="Confirm Delete" v-model:visible="showDeleteDialog" modal :style="{ width: '400px' }">
              <p>Are you sure you want to delete this staff?</p>
              <div class="flex justify-end gap-2 mt-4">
                <Button label="Cancel" class="p-button-secondary" @click="cancelDelete" />
                <Button label="Delete" class="p-button-danger" @click="confirmDelete" />
              </div>
            </Dialog>
    <Toast position="top-right" />
  </div>
</template>

<script setup>
import { FilterMatchMode, FilterOperator } from '@primevue/core/api'
import { onBeforeMount, reactive, ref, watchEffect, computed } from 'vue'
import { storeToRefs } from 'pinia'
import * as XLSX from 'xlsx'
import router from '@/router'
import { usedStaffStore } from '@/store/admin/StaffStore'
import HealthComments from '@/components/admin/HealthComments.vue'
import { useAuthStore } from '../../auth/auth'
import { useToast } from 'primevue/usetoast';

const toast = useToast()

const authStore = useAuthStore()
const staffLoc = ref('')

const props = defineProps({
  dbCall: {
    type:  Boolean,
    default: false
  },
  teacherCall:{
    type: Boolean,
    default: false
  },
  nonteacherCall:{
    type: Boolean,
    default: false
  }
})
// Responsive dialog helper
const getDialogStyle = () => {
  if (typeof window !== 'undefined') {
    if (window.innerWidth < 768) {
      return { width: '95vw', maxHeight: '90vh' }
    } else if (window.innerWidth < 1024) {
      return { width: '75vw', maxHeight: '85vh' }
    }
  }
  return { width: '90vw', maxWidth: '600px', maxHeight: '80vh' }
}

const staffRankList = ref(['Master Teacher', 'Teacher 1', 'Teacher 2'])
const staffRankValue = ref('')

const staffEmploymentList = ref(['Regular', 'Part Time', 'Contractual'])
const staffemploymentValue = ref('')

const staffDepartmentList = ref(['Mathematics', 'Science', 'English','Engineering Department'])
const staffDepartmentValue = ref('')

const showDialog1 = ref(false)
const display = ref(false)
const visibleTop = ref(false)

function open(id) {
  const selected = students.value.find((student) => student.id === id)
  studentStore.addstud(selected)
  if (selected) {
    formselect.name = selected.name
    formselect.age = selected.age
    formselect.birthdate = selected.birthdate
    formselect.gender = selected.gender
    formselect.avegrade = selected.avegrade
    formselect.status = selected.status
    formselect.pass = selected.pass
    formselect.mother = selected.representative.mother
    formselect.father = selected.representative.father
    formselect.phonenumber = selected.phonenumber
    formselect.grade = selected.grade
    formselect.section = selected.section
  } else {
    alert('Student not found!')
  }
  display.value = true
}

function close() {
  display.value = false
  visibleTop.value = true
}

const staffStore = usedStaffStore()
const { staffs, teaching,nonteaching } = storeToRefs(staffStore)
onBeforeMount( async () =>{
  loading1.value = true
  if (props.dbCall && props.teacherCall){
    staffLoc.value = 'Overall teacher list'
    await staffStore.getTeacherCount(authStore.user_pid,"")

  }else if(props.dbCall && props.nonteacherCall){
    staffLoc.value = 'Overall non teaching list'
    await staffStore.getNonTeachingCount(authStore.user_pid,"")

  }
  else{
  staffLoc.value = 'Overall staff List'

  await staffStore.fetchData()


  }


staffDepartmentList.value = [
  ...new Set(
    staffs.value
      .map(item => item.department?.name)
      .filter(Boolean)
  )
];

staffEmploymentList.value = [
  ...new Set(
    staffs.value
      .map(item => item.employment_type?.name)
      .filter(Boolean)
  )
];


staffRankList.value = [
  ...new Set(
    staffs.value
      .map(item => item.rank?.name)
      .filter(Boolean)
  )
];
  loading1.value = false

})
const submitFilter  = async () => {
    await staffStore.fetchDataWithFilter(staffDepartmentValue.value,staffemploymentValue.value,staffRankValue.value)

}
const statuses = reactive(['Drop', 'Active'])
const customers1 = ref(null)
const filters1 = ref(null)
const loading1 = ref(false)

const formselect = reactive({
  First: '',
  grade: '',
  section: '',
  gender: '',
  mother: '',
  father: '',
  birthdate: '',
  avegrade: '',
  status: '',
  phonenumber: '',
  age: 0,
  pass: false,
})

const displayedStudents = ref([])

function getid(id) {
  const selected = students.value.find((student) => student.id === id)
  studentStore.addstud(selected)
  if (selected) {
    studentStore.add
    alert(selected.name)
  } else {
    alert('Student not found!')
  }
}

function alset() {
  alert(customers1.value)
}

function initFilters1() {
  filters1.value = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    first_name: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    last_name: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    middle_name: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    phone_number: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    status: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    gender: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    email_address: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    address1: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    address2: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
    brgy: {
      operator: FilterOperator.AND,
      constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }],
    },
  }
}

function formatDate(value) {
  return value.toLocaleDateString('en-US', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function getSeverity(status) {
  switch (status) {
    case 'Drop':
      return 'danger'
    case 'Active':
      return 'success'
    case 'renewal':
      return null
  }
}

const deleteIndex = ref(null);
const showDeleteDialog = ref(false);

const requestDelete = (index) => {
  deleteIndex.value = index;
  showDeleteDialog.value = true;
};

const cancelDelete = () => {
  showDeleteDialog.value = false;
  deleteIndex.value = null;
};

const confirmDelete = async () => {
  if (deleteIndex.value !== null) {
  const ret = await staffStore.deleteStaff(deleteIndex.value)

    if (ret.success) {
      toast.add({
        severity: 'success',
        summary: 'Deleted! 🎉',
        detail: `Staff deleted successfully.`,
        life: 5000
      });
    } else {
      toast.add({
        severity: 'error',
        summary: 'Failed!',
        detail: `Failed to delete staff.`,
        life: 5000
      });
    }
  }
  showDeleteDialog.value = false;
  deleteIndex.value = null;
};

const onTableFilter = (event) => {
  displayedStudents.value = event.filteredValue
}

const exportToExcel = () => {
  if (!displayedStudents.value || displayedStudents.value.length === 0) {
    alert('No data to export!')
    return
  }

  const workbook = XLSX.utils.book_new()
  const worksheetData = [
    [
      'Name',
      'School',
      'Gender',
      'Marital Status',
      'Phone Number',
      'Email',
      'Address',
      'Barangay',
    ],
    ...displayedStudents.value.map((staff) => [
      staff.mname ? `${staff.lname}, ${staff.fname} ${staff.mname}` : `${staff.lname}, ${staff.fname}`,
      staff.school?.name || '',
      staff.gender,
      staff.marital_status,
      staff.phone_number,
      staff.email,
      `${staff.address1} ${staff.address2}`.trim(),
      staff.barangay,
    ]),
  ]

  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData)
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Staff')
  XLSX.writeFile(workbook, 'Overall_Staff_list.xlsx')
}

watchEffect(() => {


  if (props.dbCall && props.teacherCall){
    staffLoc.value = 'Overall teacher list'
  customers1.value = teaching.value


  }else if(props.dbCall && props.nonteacherCall){
    staffLoc.value = 'Overall non teaching list'
  customers1.value = nonteaching.value


  }
  else{
  customers1.value = staffs.value


  }
  if (!filters1.value) {
    initFilters1()
  }
})
</script>

<style scoped>
.form-container {
  min-width: unset; /* Remove fixed min-width for mobile responsiveness */
  margin: 0 auto;
  padding: 1rem; /* Add padding for better spacing on all screens */
  max-width: 1200px; /* Optional: Constrain max width on large screens */
}

/* Custom styles for DataTable responsiveness on mobile */
/* These styles will kick in for smaller screens to make the table columns stack */
@media screen and (max-width: 960px) {
  .p-datatable.p-datatable-responsive-mobile .p-datatable-thead > tr > th,
  .p-datatable.p-datatable-responsive-mobile .p-datatable-tfoot > tr > td {
    display: none !important; /* Hide original table headers/footers */
  }

  .p-datatable.p-datatable-responsive-mobile .p-datatable-tbody > tr > td {
    text-align: left;
    display: flex;
    align-items: center;
    justify-content: space-between; /* Space out label and value */
    width: 100%;
    padding: 0.5rem 1rem;
    border: 0 none;
    border-top: 1px solid var(--surface-d); /* Separator between "rows" of data */
  }

  .p-datatable.p-datatable-responsive-mobile .p-datatable-tbody > tr > td:last-child {
    border-bottom: 1px solid var(--surface-d); /* Bottom border for the last cell in a stacked row */
  }

  .p-datatable.p-datatable-responsive-mobile .p-datatable-tbody > tr > td:first-child {
    background: var(--surface-b); /* Highlight the first item (e.g., Name) */
    font-weight: 600;
  }

  /* Label for each row data - uses the 'data-p-field' attribute from PrimeVue's internal structure */
  .p-datatable.p-datatable-responsive-mobile .p-datatable-tbody > tr > td::before {
    content: attr(data-p-field) ":"; /* Displays the column header as a label */
    font-weight: 600;
    margin-right: 0.5rem;
    display: inline-block;
  }
}

/* General layout adjustments for smaller screens */
@media (max-width: 768px) {
  /* Ensure header elements stack nicely */
  .p-datatable-header .flex {
    flex-direction: column;
    align-items: flex-start; /* Align stacked items to the start */
  }

  /* Make sure individual inputs and buttons within header take full width on mobile */
  .p-datatable-header .p-inputtext,
  .p-datatable-header .p-multiselect, /* (if MultiSelect was here) */
  .p-datatable-header .p-button {
    width: 100%;
  }

  /* Adjust gaps for stacked elements */
  .p-datatable-header .flex-column > .flex-column {
    gap: 0.75rem; /* Smaller gap for stacked inner groups */
  }
}
</style>
